import React, { useMemo } from 'react'

import { useHistory } from 'react-router-dom'

import { SxProps } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Table from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableContainer from 'components_new/atoms/Table/TableContainer'
import TableHead from 'components_new/atoms/Table/TableHead'
import TableRow from 'components_new/atoms/Table/TableRow'
import Text from 'components_new/atoms/Text'

import GenericListItem from 'components_new/molecules/GenericListItem'
import EditExportTableMenu from 'components_new/organisms/EditExportTableMenu'

import { getHighlightedMatch } from 'components_new/organisms/StandardSearch/utils'

import Empty from './empty'
import Loading from './loading'

import { useTranslation } from 'language/translator'
import {
  useExportTables,
  useExportTablesLoading
} from 'redux/hooks/ExportTables'

interface Props {
  sx?: SxProps
  search: string | null
}

const ExportTablesList = React.memo((props: Props) => {
  const { sx, search } = props

  const { t } = useTranslation()
  const history = useHistory()

  const exportTables = useExportTables()
  const { fetched } = useExportTablesLoading()

  const filteredTables = useMemo(() => {
    return exportTables.filter((table) => {
      return (
        !search ||
        table.title.toLocaleLowerCase().includes(search.toLocaleLowerCase())
      )
    })
  }, [exportTables, search])

  if (!fetched) return <Loading />

  if (filteredTables.length === 0) return <Empty />

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        position: 'relative',
        minHeight: 0,
        ...sx
      }}
    >
      <Text color="text.disabled" variant="caption">
        {t('global', 'shows')} <b>{filteredTables.length}</b>{' '}
        {t('global', 'of', true)} {exportTables.length}{' '}
        {t('export_tables', 'tables', true)}
      </Text>

      <TableContainer sx={{ flex: 1 }}>
        <Table stickyHeader={true}>
          <TableHead>
            <TableRow sx={{ ['& th']: { bgcolor: 'background.paper' } }}>
              <TableCell padding="none">
                {t('export_tables', 'table')}
              </TableCell>
              <TableCell padding="none" sx={{ width: 60 }}>
                {''}
                {/* Actions column */}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTables.map((table) => {
              return (
                <TableRow
                  hover={true}
                  key={table.id}
                  onClick={() => {
                    history.push(`/export-tables/${table.id}`)
                  }}
                  sx={{
                    cursor: 'pointer'
                  }}
                >
                  <TableCell padding="none" sx={{ py: 0.5 }}>
                    <GenericListItem
                      customTitle={getHighlightedMatch(table.title, search)}
                      disablePadding={true}
                      title={table.title}
                      variant="entity"
                    />
                  </TableCell>
                  <TableCell
                    padding="none"
                    sx={{ py: 0.5 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <EditExportTableMenu exportTable={table} />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
})

ExportTablesList.displayName = 'ExportTablesList'

export default ExportTablesList
